// ./components/Footer.jsx
"use client";
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

import logo from '../assets/images/archvision-logo.png';
import Texture from '../assets/images/texture.jpg';
import CustomFont from '../assets/fonts/GRADIOO.ttf';
import QuoteFont from '../assets/fonts/AwanNoyeed.otf';

import { Instagram } from 'lucide-react';

// ───────────────────────────────────────────────
//  ANIMATED BACKGROUND (same as HeroSection)
// ───────────────────────────────────────────────
const AnimatedBackground = () => {
  const pathVariants = {
    initial: { strokeDashoffset: 800, strokeDasharray: "50 800", opacity: 0 },
    animate: { strokeDashoffset: 0, strokeDasharray: "20 800", opacity: [0, 0.4, 0.7, 0.3, 0] },
  };

  const paths = [
    "M720 450C720 450 742.459 440.315 755.249 425.626C768.039 410.937 778.88 418.741 789.478 401.499C800.076 384.258 817.06 389.269 826.741 380.436C836.423 371.603 851.957 364.826 863.182 356.242C874.408 347.657 877.993 342.678 898.867 333.214C919.741 323.75 923.618 319.88 934.875 310.177C946.133 300.474 960.784 300.837 970.584 287.701C980.384 274.564 993.538 273.334 1004.85 263.087C1016.15 252.84 1026.42 250.801 1038.22 242.1C1050.02 233.399 1065.19 230.418 1074.63 215.721C1084.07 201.024 1085.49 209.128 1112.65 194.884C1139.8 180.64 1132.49 178.205 1146.43 170.636C1160.37 163.066 1168.97 158.613 1181.46 147.982C1193.95 137.35 1191.16 131.382 1217.55 125.645C1243.93 119.907 1234.19 118.899 1254.53 100.846C1274.86 82.7922 1275.12 92.8914 1290.37 76.09C1305.62 59.2886 1313.91 62.1868 1323.19 56.7536C1332.48 51.3204 1347.93 42.8082 1361.95 32.1468C1375.96 21.4855 1374.06 25.168 1397.08 10.1863C1420.09 -4.79534 1421.41 -3.16992 1431.52 -15.0078",
    // ... paste the remaining 6 paths from your HeroSection here
  ];

  const colors = [
    "#630000", "#1B1717", "#D6590C", "#8B4513", "#696969",
    "#A0522D", "#B22222", "#2F4F4F", "#800000", "#556B2F"
  ];

  return (
    <motion.svg
      viewBox="0 0 1440 900"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ opacity: 0, scale: 1.1 }}
      animate={{ opacity: 0.25, scale: 1 }}
      transition={{ duration: 4, ease: "easeOut", delay: 0.8 }}
      className="absolute inset-0 w-full h-full pointer-events-none"
    >
      {paths.map((path, idx) => (
        <React.Fragment key={idx}>
          <motion.path
            d={path}
            stroke={colors[idx % colors.length]}
            strokeWidth="1.6"
            strokeLinecap="round"
            fill="none"
            variants={pathVariants}
            initial="initial"
            animate="animate"
            transition={{
              duration: 16,
              ease: "easeInOut",
              repeat: Infinity,
              delay: 1.5 + idx * 0.5,
              repeatDelay: Math.random() * 6 + 3,
            }}
          />
          <motion.path
            d={path}
            stroke={colors[(idx + 4) % colors.length]}
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
            variants={pathVariants}
            initial="initial"
            animate="animate"
            transition={{
              duration: 20,
              ease: "easeInOut",
              repeat: Infinity,
              delay: 2.5 + idx * 0.6,
              repeatDelay: Math.random() * 8 + 4,
            }}
          />
        </React.Fragment>
      ))}
    </motion.svg>
  );
};

const Footer = () => {
  const shouldReduceMotion = useReducedMotion();

  // Text styles matching HeroSection
  const fontStyles = `
    @font-face {
      font-family: 'CustomGrad';
      src: url(${CustomFont}) format('truetype');
      font-weight: 700;
      font-style: normal;
      font-display: swap;
    }
    @font-face {
      font-family: 'QuoteFont';
      src: url(${QuoteFont}) format('truetype');
      font-weight: 400;
      font-style: normal;
      font-display: swap;
    }
  `;

  const mainTexture = {
    backgroundImage: `url(${Texture})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    color: 'transparent',
    WebkitTextFillColor: 'transparent',
    fontFamily: 'CustomGrad, sans-serif',
  };

  const footerLinks = [
    {
      label: 'Navigation',
      links: [
        { title: 'Home', href: '#' },
        { title: 'About', href: '#about' },

      ],
    },
    {
      label: 'Services',
      links: [
        { title: 'Web Development', href: '#web' },
        { title: 'UI/UX Design', href: '#uiux' },
        { title: 'Backend Solutions', href: '#backend' },
        { title: 'Consulting', href: '#consult' },
        { title: 'Portfolio Creator', href: '#portfolio' },
      ],
    },
    {
      label: 'Connect',
      links: [
        { title: 'Instagram', href: 'https://instagram.com', icon: Instagram },
      ],
    },
  ];

  return (
    <footer className="relative bg-[#EDEBDD] text-[#1B1717] overflow-hidden border-t border-[#630000]/30">
      <AnimatedBackground />

      <style>{fontStyles}</style>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-16 md:py-20">
        <div className="grid md:grid-cols-4 gap-10 md:gap-12">
          {/* Logo + Tagline */}
          <motion.div
            initial={shouldReduceMotion ? {} : { opacity: 0, y: 30, filter: 'blur(6px)' }}
            whileInView={shouldReduceMotion ? {} : { opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="space-y-4"
          >
            <img src={logo} alt="Logo" className="h-14 w-auto" />
            <p style={mainTexture} className="text-xl md:text-2xl font-bold tracking-tight">
              GL1TCH
            </p>
            <p className="text-[#1B1717]/80 text-sm leading-relaxed max-w-xs">
              © 2026 • Webfolio.ma — Founded & owned by GL1tch
            </p>
          </motion.div>

          {/* Link Columns */}
          {footerLinks.map((group, idx) => (
            <motion.div
              key={group.label}
              initial={shouldReduceMotion ? {} : { opacity: 0, y: 30, filter: 'blur(6px)' }}
              whileInView={shouldReduceMotion ? {} : { opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3 + idx * 0.15 }}
            >
              <h4 style={mainTexture} className="text-2xl font-bold mb-5 tracking-tight">
                {group.label}
              </h4>
              <ul className="space-y-3 text-[#1B1717]/80">
                {group.links.map((link) => (
                  <li key={link.title}>
                    <a
                      href={link.href}
                      className="inline-flex items-center gap-2 hover:text-[#D6590C] transition-colors duration-300"
                    >
                      {link.icon && <link.icon className="w-4 h-4" />}
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;